import { useState, useEffect } from "react";

import { CSSProperty } from "@/types/property-panel.types";
import {
  stripPx,
  parseTextShadow,
  parseBoxShadow,
  fontFamilyOptions,
} from "@/lib/visual-editor/helper";

interface Element {
  source?: any;
  text?: string;
  content?: string;
  styles?: Record<string, string>;
  attributes?: Record<string, any>;
  tagName?: string;
}

export interface VideoSettings {
  autoplay: boolean;
  muted: boolean;
  loop: boolean;
  controls: boolean;
  playsInline: boolean;
  poster: string;
  preload: "auto" | "metadata" | "none";
}

const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  autoplay: false,
  muted: false,
  loop: false,
  controls: true,
  playsInline: false,
  poster: "",
  preload: "metadata",
};

/** Match a computed fontFamily string to the closest known option.
 *  getComputedStyle returns the full resolved stack e.g.
 *  "Inter, ui-sans-serif, system-ui, sans-serif, ..." but our
 *  options only store "Inter, sans-serif". We extract the primary
 *  font name and find the matching option. */
function matchFontFamily(computed: string): string {
  if (!computed) return "Inter, sans-serif";
  const cleaned = computed.replace(/["']/g, "").trim();
  // Try exact match first
  const exact = fontFamilyOptions.find((f) => f.value === cleaned);
  if (exact) return exact.value;
  // Extract primary font (first entry before comma)
  const primary = cleaned.split(",")[0].trim();
  const match = fontFamilyOptions.find(
    (f) => f.value.split(",")[0].trim() === primary,
  );
  return match?.value || "none";
}

export const usePropertyPanelState = (element: Element | null) => {
  const [content, setContent] = useState("");
  const [styles, setStyles] = useState<Record<string, string>>({
    marginTop: "0px",
    marginBottom: "0px",
    marginLeft: "0px",
    marginRight: "0px",
    paddingTop: "0px",
    paddingBottom: "0px",
    paddingLeft: "0px",
    paddingRight: "0px",
    fontSize: "48px",
    fontWeight: "500",
    color: "inherit",
    backgroundColor: "inherit",
    textAlign: "left",
    letterSpacing: "normal",
    lineHeight: "0px",
    opacity: "1",
    width: "auto",
    height: "auto",
    borderColor: "transparent",
    borderWidth: "0px",
    borderStyle: "solid",
    borderRadius: "0px",
    borderTopLeftRadius: "0px",
    borderTopRightRadius: "0px",
    borderBottomLeftRadius: "0px",
    borderBottomRightRadius: "0px",
  });
  const [cssProperties, setCssProperties] = useState<CSSProperty[]>([]);
  const [visibility, setVisibility] = useState<
    "none" | "desktop" | "tablet" | "mobile"
  >("none");
  const [textShadow, setTextShadow] = useState({
    color: "rgb(0, 0, 0)",
    x: 0,
    y: 0,
    blur: 0,
  });
  const [boxShadow, setBoxShadow] = useState({
    color: "rgb(0, 0, 0)",
    x: 0,
    y: 0,
    blur: 0,
    spread: 0,
  });
  const [linkUrlButton, setLinkUrlButton] = useState<string>("");
  const [imageSrc, setImageSrc] = useState<string>("");
  const [classNames, setClassNames] = useState<string>("");
  const [localOpacity, setLocalOpacity] = useState<number>(100);
  const [videoSettings, setVideoSettings] = useState<VideoSettings>(
    DEFAULT_VIDEO_SETTINGS,
  );
  const [iconName, setIconName] = useState<string>("");
  const [elementId, setElementId] = useState<string>("");

  // Parse visibility from className
  const parseVisibility = (
    classes: string[],
  ): "none" | "desktop" | "tablet" | "mobile" => {
    const hasHidden = classes.includes("hidden");
    const hasBlock = classes.includes("block");
    const hasSmBlock = classes.includes("sm:block");
    const hasMdHidden = classes.includes("md:hidden");
    const hasLgBlock = classes.includes("lg:block");
    const hasLgHidden = classes.includes("lg:hidden");

    if (hasHidden && hasSmBlock && !hasMdHidden && !hasLgHidden) {
      return "mobile";
    } else if (hasBlock && hasMdHidden && hasLgBlock) {
      return "tablet";
    } else if ((hasBlock || !hasHidden) && hasLgHidden) {
      return "desktop";
    }
    return "none";
  };

  // Sync state with element changes
  useEffect(() => {
    if (element?.source) {
      const newTextShadow = element.styles?.textShadow
        ? parseTextShadow(element.styles.textShadow)
        : { color: "rgb(0, 0, 0)", x: 0, y: 0, blur: 0 };
      const newBoxShadow = element.styles?.boxShadow
        ? parseBoxShadow(element.styles.boxShadow)
        : { color: "rgb(0, 0, 0)", x: 0, y: 0, blur: 0, spread: 0 };

      setTextShadow(newTextShadow);
      setBoxShadow(newBoxShadow);
      setLinkUrlButton(element.attributes?.href || "");
      setImageSrc(element.attributes?.src || "");
      setClassNames(element.attributes?.class || "");
      setIconName(element.attributes?.["data-icon-name"] || "");
      setElementId(element.attributes?.id || "");
      setContent(element.text || element.content || "");

      // Parse visibility from className
      const classes = (element.attributes?.class || "").split(" ");
      const detectedVisibility = parseVisibility(classes);
      setVisibility(detectedVisibility);

      setStyles({
        marginTop: stripPx(element.styles?.marginTop),
        marginBottom: stripPx(element.styles?.marginBottom),
        marginLeft: stripPx(element.styles?.marginLeft),
        marginRight: stripPx(element.styles?.marginRight),
        paddingTop: stripPx(element.styles?.paddingTop),
        paddingBottom: stripPx(element.styles?.paddingBottom),
        paddingLeft: stripPx(element.styles?.paddingLeft),
        paddingRight: stripPx(element.styles?.paddingRight),
        fontSize: element.styles?.fontSize || "48px",
        fontWeight: element.styles?.fontWeight || "500",
        color: element.styles?.color || "inherit",
        backgroundColor: element.styles?.backgroundColor || "inherit",
        textAlign: element.styles?.textAlign || "left",
        letterSpacing: element.styles?.letterSpacing || "normal",
        lineHeight: element.styles?.lineHeight || "0px",
        opacity: element.styles?.opacity || "1",
        width: element.styles?.width || "auto",
        height: element.styles?.height || "auto",
        minWidth: element.styles?.minWidth || "0px",
        minHeight: element.styles?.minHeight || "0px",
        maxWidth: element.styles?.maxWidth || "none",
        maxHeight: element.styles?.maxHeight || "none",
        fontFamily: matchFontFamily(element.styles?.fontFamily || ""),
        borderColor: element.styles?.borderColor || "transparent",
        borderWidth: element.styles?.borderWidth || "0px",
        borderStyle: element.styles?.borderStyle || "solid",
        borderRadius: element.styles?.borderRadius || "0px",
        borderTopLeftRadius: element.styles?.borderTopLeftRadius || "0px",
        borderTopRightRadius: element.styles?.borderTopRightRadius || "0px",
        borderBottomLeftRadius: element.styles?.borderBottomLeftRadius || "0px",
        borderBottomRightRadius:
          element.styles?.borderBottomRightRadius || "0px",
        objectFit: element.styles?.objectFit || "fill",
        display: element.styles?.display || "",
        flexDirection: element.styles?.flexDirection || "",
        justifyContent: element.styles?.justifyContent || "",
        alignItems: element.styles?.alignItems || "",
        flexWrap: element.styles?.flexWrap || "",
        gap: element.styles?.gap || "",
        gridTemplateColumns: element.styles?.gridTemplateColumns || "",
      });

      // Sync localOpacity with element opacity
      const elementOpacity = parseFloat(element.styles?.opacity || "1");
      setLocalOpacity(Math.round(elementOpacity * 100));

      // Sync video/audio settings from element attributes
      if (element.tagName === "video" || element.tagName === "audio") {
        const attrs = element.attributes || {};
        // DOM lowercases playsInline → playsinline
        const hasBoolAttr = (name: string): boolean =>
          attrs[name] !== undefined && attrs[name] !== null;
        setVideoSettings({
          autoplay: hasBoolAttr("autoplay"),
          muted: hasBoolAttr("muted"),
          loop: hasBoolAttr("loop"),
          controls: hasBoolAttr("controls"),
          playsInline: hasBoolAttr("playsinline"),
          poster: attrs.poster || "",
          preload: attrs.preload || "metadata",
        });
      }
    }
  }, [element?.source]);

  return {
    content,
    setContent,
    styles,
    setStyles,
    cssProperties,
    setCssProperties,
    visibility,
    setVisibility,
    textShadow,
    setTextShadow,
    boxShadow,
    setBoxShadow,
    linkUrlButton,
    setLinkUrlButton,
    imageSrc,
    setImageSrc,
    classNames,
    setClassNames,
    localOpacity,
    setLocalOpacity,
    videoSettings,
    setVideoSettings,
    iconName,
    setIconName,
    elementId,
    setElementId,
  };
};
